import { IAppState } from '../../core/redux/IAppState';
import { GuestComment } from './guestComment';
import * as R from 'ramda';
import { isNilOrEmpty } from '../../language/module';
import { MAX_COMMENT_CHARS, MIN_COMMENT_CHARS } from './constants';
import { getErrorShortCircuit, getLoadingShortCircuit } from '../../core/redux/api/module';
import { ACTION_TYPE } from '../../core/redux/actionType';
import { getResource } from '../../language/resources/getResource';


/**
 * Returns all comments associated with a given guest by the guest's id.
 * If a guest doesn't have any comments associated with them by guest id then it returns an empty array
 * @param state
 * @param guestId
 */
export const getGuestCommentHistory = (
  state: IAppState,
  guestId: string | undefined,
): GuestComment[] => R.filter(
  history => history.guestId === guestId,
  state.commentHistories || [],
);


/**
 * Whether or not the given hotlist comments are valid.
 * Currently, valid is 10-500 chars, not undefined.
 */
export const isValidGuestComment = (
  comments: string | undefined,
): boolean => !!(comments && comments.length >= MIN_COMMENT_CHARS && comments.length <= MAX_COMMENT_CHARS);


/**
 * Validates the given submission details for the case when an agent is adding a comment to a guest for other guest
 * agents to read down the road. Undefined inputs are allowed. Trims inputs before validation. Returns a validation
 * message to display to the user if something is wrong. If everything checks out, returns undefined.
 */
export const validateAddGuestCommentsSubmission = (
  resources: Record<string, any>,
  subject: string | undefined,
  comments: string | undefined,
): string | undefined => {
  if (isNilOrEmpty((subject || '').trim())) {
    return getResource(resources, 'COMMENT_NO_SUBJECT');
  }

  if (!isValidGuestComment((comments || '').trim())) {
    return getResource(resources, 'COMMENT_NO_COMMENT');
  }

  return undefined;
};


/**
 * Gets the loading comment history for the given Guest ID if it exists, and if the guest ID isn't undefined. Perfect
 * for if you don't know if you even have a guest.
 */
export const getLoadingCommentHistory = getLoadingShortCircuit(ACTION_TYPE.GUEST_COMMENTS_REQUEST);


/**
 * Gets the loading comment history for the given Guest ID if it exists, and if the guest ID isn't undefined. Perfect
 * for if you don't know if you even have a guest.
 */
export const getErroredCommentHistory = getErrorShortCircuit(ACTION_TYPE.GUEST_COMMENTS_REQUEST);


/**
 * Gets the loading comment history for the given Guest ID if it exists, and if the guest ID isn't undefined. Perfect
 * for if you don't know if you even have a guest.
 */
export const getLoadingAddCommentHistory = getLoadingShortCircuit(ACTION_TYPE.ADD_GUEST_COMMENT_REQUEST);


/**
 * Gets the loading comment history for the given Guest ID if it exists, and if the guest ID isn't undefined. Perfect
 * for if you don't know if you even have a guest.
 */
export const getErroredAddCommentHistory = getErrorShortCircuit(ACTION_TYPE.ADD_GUEST_COMMENT_REQUEST);
